/**
 * Convert Firebase SDK errors (Firestore, Auth, callable) into user-friendly toast text.
 * Prevents raw technical messages like "Missing or insufficient permissions." from leaking to the UI.
 */

const PERMISSION_RE = /missing or insufficient permissions|permission.denied/i;
const UNAVAILABLE_RE = /unavailable|deadline.exceeded|resource.exhausted/i;
const UNAUTHENTICATED_RE = /unauthenticated|not.authenticated/i;
const NETWORK_RE = /failed to fetch|network error|aborted|net::err|cors/i;
const NOT_FOUND_RE = /not.found|no document to update/i;
const QUOTA_RE = /quota|rate.limit|too many requests/i;
const INTERNAL_RE = /^internal$|functions\/internal/i;
const DISPATCH_CONFIG_RE = /runpod_endpoint_id|runpod_api_key|serverless dispatch not configured/i;

export function isOpaqueInternalErrorText(value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const code = typeof value === 'object' && value != null ? String(value.code || '').trim() : '';
  return INTERNAL_RE.test(raw) || INTERNAL_RE.test(code);
}

/** Strip Firebase/HTTP opaque errors before showing copy in the UI. */
export function sanitizeUserFacingErrorText(value, fallback = 'Something went wrong. Try again.') {
  if (value == null) return '';
  if (typeof value === 'object') {
    return callableErrorMessage(value, fallback);
  }
  const raw = String(value).trim();
  if (!raw) return '';
  if (INTERNAL_RE.test(raw)) {
    return 'Could not reach the server. Check your connection and try again.';
  }
  return raw;
}

/** User-facing copy when dispatch / callable processing cannot start or finish. */
export const TRANSPARENT_PROCESSING_FAILURE_MESSAGE =
  "We couldn't process this capture. Please try again or contact support.";

export default function firebaseErrorMessage(err, fallback = 'Something went wrong. Try again.') {
  if (!err) return fallback;

  const raw = typeof err === 'string' ? err : (err.message ?? '');
  const code = err?.code ?? '';
  const combined = `${code} ${raw}`;

  if (PERMISSION_RE.test(combined) || code === 'permission-denied')
    return 'You don\u2019t have permission for this action. Try signing out and back in.';

  if (UNAUTHENTICATED_RE.test(combined) || code === 'unauthenticated')
    return 'Your session expired. Please sign in again.';

  if (NETWORK_RE.test(combined) || INTERNAL_RE.test(raw) || INTERNAL_RE.test(code))
    return 'Could not reach the server. Check your connection and try again.';

  if (UNAVAILABLE_RE.test(combined))
    return TRANSPARENT_PROCESSING_FAILURE_MESSAGE;

  if (QUOTA_RE.test(combined))
    return 'Too many requests. Wait a moment and try again.';

  if (DISPATCH_CONFIG_RE.test(combined))
    return TRANSPARENT_PROCESSING_FAILURE_MESSAGE;

  if (NOT_FOUND_RE.test(combined))
    return 'The requested item was not found. It may have been deleted.';

  return raw || fallback;
}

/**
 * Callable / HttpsError messages: preserve server text for quota, precondition, and
 * permission codes so users see "Free plan limit reached…" instead of a generic unavailable message.
 */
export const DISPATCH_FAILED_FAILURE_CODE = 'DISPATCH_FAILED';

const DISPATCH_RETRY_GPU_SUBMIT_RE =
  /gpu job submission failed|gpu backend is unreachable|could not be recorded|could not start processing/i;

/** Persist on capture docs when dispatchCapture fails before a RunPod job id exists. */
export function dispatchCallableFailureDetail(error) {
  if (!error) return 'Dispatch failed before GPU job could start';
  const code = String(error.code || '')
    .replace(/^functions\//, '')
    .trim();
  const msg = typeof error.message === 'string' ? error.message.trim() : '';
  if (code && msg) return `${code}: ${msg}`.slice(0, 4000);
  return (msg || code || 'Dispatch failed before GPU job could start').slice(0, 4000);
}

/** One automatic client retry after upload when the callable error is likely transient. */
export function isRetryableDispatchCallableError(error) {
  if (!error) return false;
  const code = String(error.code || '').toLowerCase();
  const msg = String(error.message || '').toLowerCase();
  if (code.includes('unavailable') || code.includes('deadline-exceeded')) return true;
  if (NETWORK_RE.test(msg) || NETWORK_RE.test(code)) return true;
  if (DISPATCH_RETRY_GPU_SUBMIT_RE.test(msg)) return true;
  if (code.includes('internal') && msg.includes('try again')) return true;
  return false;
}

export function callableErrorMessage(error, fallback = 'Something went wrong. Try again.') {
  if (!error) return fallback;
  const code = String(error?.code || '');
  const msg = typeof error.message === 'string' && error.message.trim() ? error.message.trim() : '';
  if (INTERNAL_RE.test(code) || INTERNAL_RE.test(msg)) {
    return firebaseErrorMessage(error, fallback);
  }
  if (DISPATCH_CONFIG_RE.test(`${code} ${msg}`)) {
    return TRANSPARENT_PROCESSING_FAILURE_MESSAGE;
  }
  if (
    msg
    && (
      code.includes('functions/')
      || code === 'resource-exhausted'
      || code === 'failed-precondition'
      || code === 'permission-denied'
      || code === 'already-exists'
    )
  ) {
    return msg;
  }
  return firebaseErrorMessage(error, fallback);
}
